import React from "react";
import { Box, Typography } from "@mui/material";

// Gráfico de linhas em SVG puro, na mesma linguagem dos gráficos do Dashboard
// (sem biblioteca externa). Suporta várias séries, trecho tracejado (projeção)
// e banda de confiança (polígono translúcido).

export interface ChartSeries {
  name: string;
  color: string;
  values: (number | null)[];
  dashed?: boolean;
}

export interface ChartBand {
  color: string;
  low: (number | null)[];
  high: (number | null)[];
}

interface MultiLineChartProps {
  labels: string[];
  series: ChartSeries[];
  band?: ChartBand;
  height?: number;
  valueFormatter?: (value: number) => string;
  showValues?: boolean;
  labelFormatter?: (value: number) => string;
  /** Texto do eixo X por rótulo; sem ele, o rótulo é tratado como mês (yyyy-MM). */
  labelText?: (label: string) => string;
}

const WIDTH = 860;
const PAD_LEFT = 64;
const PAD_RIGHT = 16;
const PAD_TOP = 12;
const PAD_BOTTOM = 34;

const formatMonthLabel = (month: string): string => {
  const [year, monthNumber] = month.split("-");
  const names = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${names[parseInt(monthNumber, 10) - 1]}/${year.slice(2)}`;
};

const MultiLineChart: React.FC<MultiLineChartProps> = ({
  labels,
  series,
  band,
  height = 300,
  valueFormatter = (v) => v.toLocaleString("pt-BR"),
  showValues = false,
  labelFormatter,
  labelText,
}) => {
  const pointLabel = labelFormatter ?? valueFormatter;
  const n = labels.length;
  if (n === 0) return null;

  const allValues: number[] = [];
  series.forEach((s) => s.values.forEach((v) => v !== null && allValues.push(v)));
  band?.high.forEach((v) => v !== null && allValues.push(v));
  const maxValue = Math.max(1, ...allValues) * 1.08;

  const x = (i: number) =>
    n === 1 ? WIDTH / 2 : PAD_LEFT + (i * (WIDTH - PAD_LEFT - PAD_RIGHT)) / (n - 1);
  const y = (v: number) => PAD_TOP + (height - PAD_TOP - PAD_BOTTOM) * (1 - v / maxValue);

  // segmentos contínuos (pula nulls) para cada série
  const buildSegments = (values: (number | null)[]): string[] => {
    const segments: string[] = [];
    let current: string[] = [];
    values.forEach((v, i) => {
      if (v === null) {
        if (current.length > 1) segments.push(current.join(" "));
        current = [];
      } else {
        current.push(`${x(i)},${y(v)}`);
      }
    });
    if (current.length > 1) segments.push(current.join(" "));
    return segments;
  };

  const bandPolygon = (() => {
    if (!band) return null;
    const top: string[] = [];
    const bottom: string[] = [];
    band.high.forEach((v, i) => v !== null && top.push(`${x(i)},${y(v)}`));
    for (let i = band.low.length - 1; i >= 0; i--) {
      const v = band.low[i];
      if (v !== null) bottom.push(`${x(i)},${y(v)}`);
    }
    return top.length > 1 ? [...top, ...bottom].join(" ") : null;
  })();

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * maxValue);
  const labelStep = Math.max(1, Math.ceil(n / 10));

  return (
    <Box>
      <Box sx={{ overflowX: "auto" }}>
        <svg
          viewBox={`0 0 ${WIDTH} ${height}`}
          style={{ minWidth: 560, width: "100%", height: "auto", display: "block" }}
        >
          {yTicks.map((tick, i) => (
            <g key={i}>
              <line
                x1={PAD_LEFT}
                x2={WIDTH - PAD_RIGHT}
                y1={y(tick)}
                y2={y(tick)}
                stroke="#e9ecef"
                strokeWidth={1}
              />
              <text x={PAD_LEFT - 8} y={y(tick) + 4} textAnchor="end" fontSize={11} fill="#6c757d">
                {valueFormatter(Math.round(tick))}
              </text>
            </g>
          ))}

          {labels.map((label, i) =>
            i % labelStep === 0 ? (
              <text
                key={label}
                x={x(i)}
                y={height - 10}
                textAnchor="middle"
                fontSize={11}
                fill="#6c757d"
              >
                {(labelText ?? formatMonthLabel)(label)}
              </text>
            ) : null
          )}

          {bandPolygon && <polygon points={bandPolygon} fill={band!.color} opacity={0.18} />}

          {series.map((s) =>
            buildSegments(s.values).map((points, segIndex) => (
              <polyline
                key={`${s.name}-${segIndex}`}
                points={points}
                fill="none"
                stroke={s.color}
                strokeWidth={1.5}
                strokeDasharray={s.dashed ? "6 5" : undefined}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))
          )}

          {series.map((s) =>
            s.values.map((v, i) =>
              v !== null ? (
                <circle key={`${s.name}-${i}`} cx={x(i)} cy={y(v)} r={2.2} fill={s.color}>
                  <title>{`${s.name} — ${(labelText ?? formatMonthLabel)(labels[i])}: ${valueFormatter(v)}`}</title>
                </circle>
              ) : null
            )
          )}

          {showValues &&
            series.map((s) =>
              s.values.map((v, i) =>
                v !== null ? (
                  <text
                    key={`label-${s.name}-${i}`}
                    x={x(i)}
                    y={y(v) - 6}
                    textAnchor="middle"
                    fontSize={9}
                    fill={s.color}
                    fontWeight={600}
                  >
                    {pointLabel(v)}
                  </text>
                ) : null
              )
            )}
        </svg>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
        {series.map((s) => (
          <Box key={s.name} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 14,
                height: 3,
                borderRadius: 2,
                background: s.color,
                border: s.dashed ? `1px dashed ${s.color}` : "none",
              }}
            />
            <Typography sx={{ fontSize: 12, color: "#495057" }}>{s.name}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MultiLineChart;
